import { useEffect, useState } from "react";
import api, { fmtDate, fmtMoney, fmtErr, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { toast } from "sonner";

const LABELS = {
  clarifying: "Owner answering questions",
  submitted: "Needs your quote",
  awaiting_approval: "Waiting on owner",
  approved: "Approved",
  completed: "Completed",
  declined: "Declined",
};

const Card = ({ i, quotes, setQuotes, settings, onSuggest, onQuote, onDecline, onOperatorApprove }) => (
    <div className="card-flat space-y-3 p-4" data-testid={`addon-${i.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{i.property_name}</p>
          <p className="text-sm">{i.description}</p>
        </div>
        <span className="shrink-0 rounded-full bg-muted px-3 py-1 text-xs font-bold">{LABELS[i.status]}</span>
      </div>
      {i.qa?.length > 0 && (
        <div className="rounded-md bg-muted p-3 text-xs space-y-1">
          {i.qa.map((qa) => <p key={qa.q}><span className="font-semibold">{qa.q}</span> — {qa.a}</p>)}
        </div>
      )}
      {i.photo && <img src={photoSrc(i.photo)} alt="" className="h-24 rounded-md object-cover" />}
      <p className="text-xs text-muted-foreground">Mode: {i.mode === "standalone" ? "Standalone visit (urgent)" : "Attach to next visit"} {i.visit_date ? `· Visit ${fmtDate(i.visit_date)}` : ""}</p>
      {i.status === "submitted" && !i.has_agreement && (
        <p className="rounded-md bg-[hsl(43,74%,50%)]/15 p-3 text-xs font-semibold" data-testid={`surcharge-hint-${i.id}`}>
          No seasonal agreement for this property — ad-hoc one-time jobs locally carry a {settings?.one_time_surcharge_pct ?? 60}% surcharge.
        </p>
      )}
      {i.status === "submitted" && (
        <div className="flex flex-wrap gap-2">
          <input data-testid={`quote-input-${i.id}`} type="number" inputMode="decimal" placeholder="Est. cost $" className="field-input min-w-0 flex-[1_1_100%]"
            value={quotes[i.id] || ""} onChange={(e) => setQuotes({ ...quotes, [i.id]: e.target.value })} />
          <button data-testid={`suggest-btn-${i.id}`} onClick={() => onSuggest(i.id)} className="h-12 flex-1 rounded-md border border-border px-3 text-sm font-bold whitespace-nowrap">Suggest</button>
          <button data-testid={`quote-btn-${i.id}`} onClick={() => onQuote(i.id)} className="h-12 flex-[2_1_0%] rounded-md bg-primary px-3 text-sm font-bold text-primary-foreground whitespace-nowrap">Accept & quote</button>
          <button data-testid={`decline-btn-${i.id}`} onClick={() => onDecline(i.id)} className="h-12 flex-1 rounded-md border border-border px-3 text-sm font-bold whitespace-nowrap">Decline</button>
        </div>
      )}
      {i.status === "awaiting_approval" && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-[hsl(43,74%,40%)]">Quoted {fmtMoney(i.quoted_cost)} — waiting on owner</p>
          <button data-testid={`operator-approve-${i.id}`} onClick={() => onOperatorApprove(i.id)}
            className="h-12 w-full rounded-md border-2 border-primary text-sm font-bold text-primary">
            Confirmed with owner — approve for them
          </button>
        </div>
      )}
      {i.status === "approved" && <p className="text-sm font-semibold text-primary">Approved{i.auto_approved ? " (auto, under threshold)" : ""} · {fmtMoney(i.quoted_cost)}</p>}
    </div>
  );

export default function Instructions() {
  const [items, setItems] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [approveForms, setApproveForms] = useState({});

  const load = () => {
    api.get("/instructions").then((r) => setItems(r.data)).catch(() => toast.error("Could not load"));
    api.get("/booking-requests").then((r) => setBookings(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const quote = async (id) => {
    try {
      const r = await api.post(`/instructions/${id}/quote`, { cost: Number(quotes[id] || 0) });
      toast.success(r.data.status === "approved" ? "Auto-approved under owner's threshold — attached to visit" : "Sent to owner for approval");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const suggest = async (id) => {
    try {
      const r = await api.get(`/instructions/${id}/price-suggestion`);
      setQuotes((q) => ({ ...q, [id]: String(r.data.price) }));
      toast.success(`Suggested $${r.data.price} — ${r.data.rationale}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const decline = async (id) => {
    await api.post(`/instructions/${id}/operator-decline`);
    toast.success("Declined");
    load();
  };

  const operatorApprove = async (id) => {
    try {
      await api.post(`/instructions/${id}/operator-approve`);
      toast.success("Confirmed on the owner's behalf — attached to a visit");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const approveBooking = async (b) => {
    const f = approveForms[b.id] || {};
    try {
      await api.post(`/booking-requests/${b.id}/approve`, { date: f.date || b.requested_date, price: Number(f.price ?? b.suggested_price ?? 0) });
      toast.success("Booked — visit is on the schedule and the owner was emailed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const declineBooking = async (id) => {
    const reason = window.prompt("Reason (shared with the owner):", "Not available right now");
    if (reason === null) return;
    try { await api.post(`/booking-requests/${id}/decline`, { reason }); toast.success("Declined — owner notified"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (!items) return <Layout title="Add-ons"><p className="text-muted-foreground">Loading…</p></Layout>;
  const open = items.filter((i) => ["submitted", "awaiting_approval", "approved"].includes(i.status));
  const rest = items.filter((i) => !open.includes(i));

  const cardProps = { quotes, setQuotes, settings, onSuggest: suggest, onQuote: quote, onDecline: decline, onOperatorApprove: operatorApprove };

  return (
    <Layout title="Add-ons">
      {bookings.filter((b) => b.status === "pending").length > 0 && (
        <>
          <p className="label-caps mb-3">Booking requests</p>
          <div className="mb-8 space-y-3" data-testid="booking-requests">
            {bookings.filter((b) => b.status === "pending").map((b) => (
              <div key={b.id} className="card-flat space-y-3 border-l-4 border-l-primary p-4" data-testid={`booking-req-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{b.property_name} — {b.service_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {b.owner_name} · {b.speed === "rush" ? "Rush (same day)" : b.speed === "express" ? "Express (next 2–3 days)" : "Flexible"} · wants {fmtDate(b.requested_date)}
                    </p>
                    {b.notes && <p className="mt-1 text-xs text-muted-foreground">"{b.notes}"</p>}
                  </div>
                  <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary" data-testid={`booking-suggested-${b.id}`}>sugg. {fmtMoney(b.suggested_price)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs font-bold text-muted-foreground">Date
                    <input data-testid={`booking-date-${b.id}`} type="date" className="field-input mt-1 w-full"
                      value={approveForms[b.id]?.date ?? b.requested_date ?? ""}
                      onChange={(e) => setApproveForms({ ...approveForms, [b.id]: { ...approveForms[b.id], date: e.target.value } })} />
                  </label>
                  <label className="block text-xs font-bold text-muted-foreground">Price $
                    <input data-testid={`booking-price-${b.id}`} type="number" inputMode="decimal" className="field-input mt-1 w-full"
                      value={approveForms[b.id]?.price ?? b.suggested_price ?? ""}
                      onChange={(e) => setApproveForms({ ...approveForms, [b.id]: { ...approveForms[b.id], price: e.target.value } })} />
                  </label>
                  <button data-testid={`booking-approve-${b.id}`} onClick={() => approveBooking(b)} className="h-12 rounded-md bg-primary px-3 text-sm font-bold text-primary-foreground">Approve & schedule</button>
                  <button data-testid={`booking-decline-${b.id}`} onClick={() => declineBooking(b.id)} className="h-12 rounded-md border border-border px-3 text-sm font-bold">Decline</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="space-y-3" data-testid="addons-open">
        {open.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No open add-on requests.</p>}
        {open.map((i) => <Card key={i.id} i={i} {...cardProps} />)}
      </div>
      {rest.length > 0 && (
        <>
          <p className="label-caps mb-3 mt-8">History</p>
          <div className="space-y-3">{rest.map((i) => <Card key={i.id} i={i} {...cardProps} />)}</div>
        </>
      )}
    </Layout>
  );
}
